// LivestreamViewerCameraController.jsx
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  ExclusivePrivateStatus,
  VIEWER_CAMERA_PREPARING_LEAD_SECONDS,
  VIEWER_CAMERA_START_MODAL_ID,
  VIEWER_CAMERA_MORE_MODAL_ID,
} from '../resource/liveStreamConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

import { ModalId } from '../resource/trackingConstants.js';

import LivestreamViewerCamera from '../container/LivestreamViewerCamera.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import ModalView from '../container/ModalView.js';

const LivestreamViewerCameraController = ({
  streamId = '',
  isExclusiveViewer = false,
  exclusivePrivateStatus = '',
  performingStartUnix = 0,
  hasStreamToken = false,
  isFetchingStreamToken = false,
  hasStreamTokenFetchError = false,
  isAutoPaymentEnabled = false,
  timestampOffsetSeconds = 0,
}) => {
  const [shouldMountCamera, setShouldMountCamera] = useState(false);

  useEffect(() => {
    // Once mounted, tolerate transient goal/token changes while PREPARING
    // switches to PERFORMING. Unmount only after the session ends or token loss
    // is confirmed, otherwise local tracks would be released and recaptured.
    if (shouldMountCamera) {
      const isSessionEnded =
        exclusivePrivateStatus === ExclusivePrivateStatus.COOLDOWN ||
        exclusivePrivateStatus === ExclusivePrivateStatus.DEFAULT;
      // Token is really lost when no fetch is in flight, and auto payment is
      // off or the re-fetch already failed (payment failed case).
      const isStreamTokenLost =
        !hasStreamToken &&
        !isFetchingStreamToken &&
        (!isAutoPaymentEnabled || hasStreamTokenFetchError);
      if (isSessionEnded || isStreamTokenLost) {
        setShouldMountCamera(false);
      }
      return;
    }

    if (!isExclusiveViewer || !hasStreamToken) return;

    if (exclusivePrivateStatus === ExclusivePrivateStatus.PERFORMING) {
      setShouldMountCamera(true);
      return;
    }

    if (
      exclusivePrivateStatus !== ExclusivePrivateStatus.PREPARING ||
      performingStartUnix <= 0
    ) {
      return;
    }

    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    const secondsUntilReveal =
      performingStartUnix - VIEWER_CAMERA_PREPARING_LEAD_SECONDS - now;
    if (secondsUntilReveal <= 0) {
      setShouldMountCamera(true);
      return;
    }

    const timerId = setTimeout(
      () => setShouldMountCamera(true),
      secondsUntilReveal * 1000
    );
    return () => clearTimeout(timerId);
  }, [
    exclusivePrivateStatus,
    hasStreamToken,
    isExclusiveViewer,
    performingStartUnix,
    shouldMountCamera,
    timestampOffsetSeconds,
    isAutoPaymentEnabled,
    isFetchingStreamToken,
    hasStreamTokenFetchError,
  ]);

  if (!shouldMountCamera) {
    return null;
  }

  return (
    <>
      <LivestreamViewerCamera streamId={streamId} />
      <ModalView
        id={VIEWER_CAMERA_START_MODAL_ID}
        modalId={ModalId.LiveStream.ModalTurnOnCameraMicrophone}
        render={() => (
          <UniversalComponent
            filename="Container_LivestreamViewerCameraStartModal"
            id={VIEWER_CAMERA_START_MODAL_ID}
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LivestreamViewerCameraStartModal.js')
            }
          />
        )}
      />
      <ModalView
        id={VIEWER_CAMERA_MORE_MODAL_ID}
        modalId={ModalId.LiveStream.ModalExclusiveMore}
        render={() => (
          <UniversalComponent
            filename="Container_LivestreamViewerCameraMoreModal"
            id={VIEWER_CAMERA_MORE_MODAL_ID}
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LivestreamViewerCameraMoreModal.js')
            }
          />
        )}
      />
    </>
  );
};

LivestreamViewerCameraController.propTypes = {
  streamId: PropTypes.string,
  isExclusiveViewer: PropTypes.bool,
  exclusivePrivateStatus: PropTypes.string,
  performingStartUnix: PropTypes.number,
  hasStreamToken: PropTypes.bool,
  isFetchingStreamToken: PropTypes.bool,
  hasStreamTokenFetchError: PropTypes.bool,
  isAutoPaymentEnabled: PropTypes.bool,
  timestampOffsetSeconds: PropTypes.number,
};

export default LivestreamViewerCameraController;
